import React, { useEffect, useState } from 'react';
import { FaEye, FaEdit, FaBan, FaDownload, FaPlus, FaSort, FaSortUp, FaSortDown, FaCheck } from 'react-icons/fa';
import dayjs from 'dayjs';
import LocationFormModal from './LocationFormModal';
import LocationViewModal from './LocationViewModal';
import Sidebar from './Sidebar';
import {  useNavigate } from 'react-router-dom';

const API_URL = '/api';

const LocationMaster = () => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showView, setShowView] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [isScrolled, setIsScrolled] = useState(false);
  const navigate = useNavigate(); 

  useEffect(() => {
    fetchLocations();
  }, []);

  // Add scroll listener for sticky header behavior
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setIsScrolled(scrollTop > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const fetchLocations = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/locations`);
      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }
      const data = await res.json();
      setLocations(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching locations:', error);
      setLocations([]);
    }
    setLoading(false);
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });

    const sortedLocations = [...locations].sort((a, b) => {
      if (a[key] === null) return 1;
      if (b[key] === null) return -1;
      
      // Handle date fields
      if (key === 'CreatedAt') {
        return direction === 'asc' 
          ? new Date(a[key]) - new Date(b[key])
          : new Date(b[key]) - new Date(a[key]);
      }
      
      // Handle numeric fields
      if (key === 'LocationCode') {
        const aNum = parseInt(a[key].split('-')[1]);
        const bNum = parseInt(b[key].split('-')[1]);
        return direction === 'asc' ? aNum - bNum : bNum - aNum;
      }

      // Handle string fields
      if (a[key] < b[key]) return direction === 'asc' ? -1 : 1;
      if (a[key] > b[key]) return direction === 'asc' ? 1 : -1;
      return 0;
    });

    setLocations(sortedLocations);
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <FaSort className="ml-1" />;
    return sortConfig.direction === 'asc' 
      ? <FaSortUp className="ml-1" /> 
      : <FaSortDown className="ml-1" />;
  };

  const handleView = (location) => {
    setSelectedLocation(location);
    setShowView(true);
  };

  const handleEdit = (location) => {
    setSelectedLocation(location.LocationID);
    setIsEdit(true);
    setShowForm(true);
  };

  const handleDeactivate = async (location) => {
    if (!window.confirm('Are you sure you want to deactivate this location?')) return;
    
    try {
      const formData = new FormData();
      formData.append('locationName', location.LocationName);
      formData.append('country', location.Country || '');
      formData.append('state', location.State || '');
      formData.append('city', location.City || '');
      formData.append('postalCode', location.PostalCode || '');
      formData.append('locationTypeID', location.LocationTypeID);
      formData.append('status', '0');
      formData.append('addressLine1', location.AddressLine1 || '');
      formData.append('addressLine2', location.AddressLine2 || '');
      formData.append('parentLocationID', location.ParentLocationID || '');
      formData.append('fileName', location.FileName || '');
      formData.append('fileType', location.FileType || '');
      formData.append('updatedBy', '1');

      const response = await fetch(`${API_URL}/location/${location.LocationID}`, {
        method: 'PUT',
        body: formData
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to deactivate location');
      }
      
      fetchLocations();
    } catch (error) {
      console.error('Error deactivating location:', error);
      alert(error.message || 'An error occurred while deactivating the location');
    }
  };

  const handleReactivate = async (location) => {
    if (!window.confirm('Are you sure you want to reactivate this location?')) return;
    
    try {
      const formData = new FormData();
      formData.append('locationName', location.LocationName);
      formData.append('country', location.Country || '');
      formData.append('state', location.State || '');
      formData.append('city', location.City || '');
      formData.append('postalCode', location.PostalCode || '');
      formData.append('locationTypeID', location.LocationTypeID);
      formData.append('status', '1');
      formData.append('addressLine1', location.AddressLine1 || '');
      formData.append('addressLine2', location.AddressLine2 || '');
      formData.append('parentLocationID', location.ParentLocationID || '');
      formData.append('fileName', location.FileName || '');
      formData.append('fileType', location.FileType || '');
      formData.append('updatedBy', '1');

      const response = await fetch(`${API_URL}/location/${location.LocationID}`, {
        method: 'PUT',
        body: formData
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to reactivate location');
      }
      
      fetchLocations();
    } catch (error) {
      console.error('Error reactivating location:', error);
      alert(error.message || 'An error occurred while reactivating the location');
    }
  };

  const handleDownloadCSV = () => {
    const headers = [
      'LocationCode', 'LocationName', 'Country', 'State', 'City', 'LocationType', 'Status', 'CreatedAt'
    ];
    const rows = locations.map(l => [
      l.LocationCode, l.LocationName, l.Country, l.State, l.City, l.LocationTypeName, 
      l.Status ? 'Active' : 'Inactive', dayjs(l.CreatedAt).format('YYYY-MM-DD HH:mm')
    ]);
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'locations.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleCreate = () => {
    setSelectedLocation(null);
    setIsEdit(false);
    setShowForm(true);
  };

  const handleFormSubmit = async (formData) => {
    if (formLoading) return; // Prevent double submission
    
    setFormLoading(true);
    try {
      if (isEdit && selectedLocation) {
        console.log('Updating location with ID:', selectedLocation);
        console.log('Form data entries:', Array.from(formData.entries()));
        
        const response = await fetch(`${API_URL}/location/${selectedLocation}`, {
          method: 'PUT',
          body: formData,
        });
        
        console.log('Update response status:', response.status);
        
        if (!response.ok) {
          const errorData = await response.json();
          console.error('Update error response:', errorData);
          throw new Error(errorData.message || 'Failed to update location');
        }
        
        const result = await response.json();
        console.log('Update success:', result);
      } else {
        console.log('Creating new location');
        const response = await fetch(`${API_URL}/location`, {
          method: 'POST',
          body: formData,
        });
        if (!response.ok) {
          const errorData = await response.json();
          if (errorData.message && errorData.message.includes('duplicate key')) {
            // Close the form and reopen it to get a fresh location code
            setShowForm(false);
            setTimeout(() => {
              setShowForm(true);
              alert('Duplicate location code detected. The form has been refreshed with a new location code. Please try again.');
            }, 100);
            return;
          }
          throw new Error(errorData.message || 'Failed to create location');
        }
      }
      setShowForm(false);
      fetchLocations();
    } catch (error) {
      console.error('Error saving location:', error);
      alert(error.message || 'An error occurred while saving the location');
    } finally {
      setFormLoading(false);
    }
  };

  const handleLogout = () => {
    navigate('/login');
  };

 return (
  <div className="min-h-screen flex bg-[#E5E1DA]">
    <Sidebar onLogout={handleLogout} />
    <main className="flex-1 p-2 md:p-8 overflow-hidden md:ml-16 transition-all duration-300">
      <div className="flex flex-col h-full">
        <div className="flex flex-col sm:flex-row justify-center sm:justify-between items-center text-center sm:items-center mb-4 md:mb-6 gap-4">
          <h1 className="text-2xl md:text-3xl font-bold text-[#2b6777]">Location Master</h1>
          <button
            className="flex items-center gap-2 bg-[#92C7CF] hover:bg-[#AAD7D9] text-white font-semibold px-3 py-2 sm:px-4 sm:py-2 rounded-lg shadow transition text-sm sm:text-base"
            onClick={handleCreate}
          >
            <FaPlus /> <span className=" sm:inline">Create New Location</span>
          </button>
        </div>
        
        <div className="flex-1 overflow-hidden">
          <div className="h-full overflow-auto overflow-x-auto scroll-smooth">
            <div className="min-w-full">
              <table className="w-full bg-white rounded-2xl shadow-lg border-separate border-spacing-0">
                <thead>
                  <tr>
                    <th 
                      className={`sticky top-0 z-20 bg-[#AAD7D9] w-auto text-[#2b6777] px-4 md:px-6 py-3 md:py-4 text-center border-r border-white text-xs md:text-sm font-bold rounded-tl-2xl cursor-pointer hover:bg-[#92C7CF] transition-all duration-300 ${isScrolled ? 'shadow-lg' : ''}`}
                      onClick={() => handleSort('LocationCode')}
                    >
                      <div className="flex items-center justify-center">
                        Location Code {getSortIcon('LocationCode')}
                      </div>
                    </th>
                    <th 
                      className={`sticky top-0 z-20 bg-[#AAD7D9] text-[#2b6777] px-4 md:px-6 py-3 md:py-4 text-center border-r border-white text-xs md:text-sm font-bold cursor-pointer hover:bg-[#92C7CF] transition-all duration-300 ${isScrolled ? 'shadow-lg' : ''}`}
                      onClick={() => handleSort('LocationName')}
                    >
                      <div className="flex items-center justify-center">
                        Location Name {getSortIcon('LocationName')}
                      </div>
                    </th>
                    <th 
                      className={`sticky top-0 z-20 bg-[#AAD7D9] text-[#2b6777] px-4 md:px-6 py-3 md:py-4 text-center border-r border-white text-xs md:text-sm font-bold cursor-pointer hover:bg-[#92C7CF] transition-all duration-300 ${isScrolled ? 'shadow-lg' : ''}`}
                      onClick={() => handleSort('Country')}
                    >
                      <div className="flex items-center justify-center">
                        Country {getSortIcon('Country')}
                      </div>
                    </th>
                    <th 
                      className={`sticky top-0 z-20 bg-[#AAD7D9] text-[#2b6777] px-4 md:px-6 py-3 md:py-4 text-center border-r border-white text-xs md:text-sm font-bold cursor-pointer hover:bg-[#92C7CF] transition-all duration-300 ${isScrolled ? 'shadow-lg' : ''}`}
                      onClick={() => handleSort('State')}
                    >
                      <div className="flex items-center justify-center">
                        State {getSortIcon('State')}
                      </div>
                    </th>
                    <th 
                      className={`sticky top-0 z-20 bg-[#AAD7D9] text-[#2b6777] px-4 md:px-6 py-3 md:py-4 text-center border-r border-white text-xs md:text-sm font-bold cursor-pointer hover:bg-[#92C7CF] transition-all duration-300 ${isScrolled ? 'shadow-lg' : ''}`}
                      onClick={() => handleSort('City')}
                    >
                      <div className="flex items-center justify-center">
                        City {getSortIcon('City')}
                      </div>
                    </th>
                    <th 
                      className={`sticky top-0 z-20 bg-[#AAD7D9] text-[#2b6777] px-4 md:px-6 py-3 md:py-4 text-center border-r border-white text-xs md:text-sm font-bold cursor-pointer hover:bg-[#92C7CF] transition-all duration-300 ${isScrolled ? 'shadow-lg' : ''}`}
                      onClick={() => handleSort('LocationTypeName')}
                    >
                      <div className="flex items-center justify-center">
                        Location Type {getSortIcon('LocationTypeName')}
                      </div>
                    </th>
                    <th 
                      className={`sticky top-0 z-20 bg-[#AAD7D9] text-[#2b6777] px-4 md:px-6 py-3 md:py-4 text-center border-r border-white text-xs md:text-sm font-bold cursor-pointer hover:bg-[#92C7CF] transition-all duration-300 ${isScrolled ? 'shadow-lg' : ''}`}
                      onClick={() => handleSort('Status')}
                    >
                      <div className="flex items-center justify-center">
                        Status {getSortIcon('Status')}
                      </div>
                    </th>
                    <th 
                      className={`sticky top-0 z-20 bg-[#AAD7D9] text-[#2b6777] px-4 md:px-6 py-3 md:py-4 text-center border-r border-white text-xs md:text-sm font-bold cursor-pointer hover:bg-[#92C7CF] transition-all duration-300 ${isScrolled ? 'shadow-lg' : ''}`}
                      onClick={() => handleSort('CreatedAt')}
                    >
                      <div className="flex items-center justify-center">
                        Created Date {getSortIcon('CreatedAt')}
                      </div>
                    </th>
                    <th className={`sticky top-0 z-20 bg-[#AAD7D9] text-[#2b6777] px-4 md:px-6 py-3 md:py-4 text-center text-xs md:text-sm font-bold rounded-tr-2xl transition-all duration-300 ${isScrolled ? 'shadow-lg' : ''}`}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={9} className="text-center py-10 text-[#AAD7D9] font-semibold">
                        Loading locations...
                      </td>
                    </tr>
                  ) : locations.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="text-center py-10 text-[#AAD7D9] font-semibold">
                        No locations found.
                      </td>
                    </tr>
                  ) : locations.map((location, idx) => (
                    <tr
                      key={location.LocationID}
                      className={`transition-all duration-300 group ${location.Status ? 'hover:bg-[#eaf6f6] hover:shadow-md' : 'opacity-60'} border-b last:border-b-0 transform hover:scale-[1.01]`}
                      style={{ boxShadow: idx === locations.length - 1 ? '0 8px 24px 0 rgba(44,103,119,0.04)' : undefined }}
                    >
                      <td className="px-4 md:px-6 py-3 md:py-4 text-center text-xs md:text-sm font-mono whitespace-nowrap flex justify-center items-center">
                        <p className='cursor-pointer transition-all duration-200 hover:scale-105' style={{ color: 'blue', borderBottom: '1px solid blue', width: 'fit-content' }} onClick={() => handleView(location)}>{location.LocationCode}</p>
                      </td>
                      <td className="px-4 md:px-6 py-3 md:py-4 text-center text-xs md:text-sm whitespace-nowrap font-semibold">{location.LocationName}</td>
                      <td className="px-4 md:px-6 py-3 md:py-4 text-center text-xs md:text-sm whitespace-nowrap">{location.Country || '-'}</td>
                      <td className="px-4 md:px-6 py-3 md:py-4 text-center text-xs md:text-sm whitespace-nowrap">{location.State || '-'}</td>
                      <td className="px-4 md:px-6 py-3 md:py-4 text-center text-xs md:text-sm whitespace-nowrap">{location.City || '-'}</td>
                      <td className="px-4 md:px-6 py-3 md:py-4 text-center text-xs md:text-sm whitespace-nowrap">{location.LocationTypeName || '-'}</td>
                      <td className="px-4 md:px-6 py-3 md:py-4 text-center text-xs md:text-sm">
                        <span className={`inline-block px-2 md:px-3 py-1 rounded-full text-xs font-bold shadow-sm transition-all duration-200
                          ${location.Status ? 'bg-[#52ab98] text-white' : 'bg-gray-300 text-gray-500'}`}>
                          {location.Status ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-4 md:px-6 py-3 md:py-4 whitespace-nowrap text-xs md:text-sm text-gray-700">
                        {location.CreatedAt ? (
                          <>
                            {dayjs(location.CreatedAt).format('YYYY-MM-DD')}
                            <br />
                            <span className="text-xs text-gray-400">{dayjs(location.CreatedAt).format('HH:mm')}</span>
                          </>
                        ) : '-'}
                      </td>
                      <td className="px-4 md:px-6 py-3 md:py-4 flex gap-2 justify-center items-center">
                        <button title="View" className="p-1 md:p-2 rounded-full hover:bg-[#c8d8e4] text-[#2b6777] transition-all duration-200 hover:scale-110" onClick={() => handleView(location)}>
                          <FaEye size={14} />
                        </button>
                        <button title="Edit" className="p-1 md:p-2 rounded-full hover:bg-[#c8d8e4] text-[#2b6777] transition-all duration-200 hover:scale-110" onClick={() => handleEdit(location)}>
                          <FaEdit size={14} />
                        </button>
                        {location.Status ? (
                          <button
                            title="Deactivate"
                            className="p-1 md:p-2 rounded-full hover:bg-red-100 text-red-500 transition-all duration-200 hover:scale-110"
                            onClick={() => handleDeactivate(location)}
                          >
                            <FaBan size={14} />
                          </button>
                        ) : (
                          <button
                            title="Reactivate"
                            className="p-1 md:p-2 rounded-full hover:bg-green-100 text-green-500 transition-all duration-200 hover:scale-110"
                            onClick={() => handleReactivate(location)}
                          >
                            <FaCheck size={14} />
                          </button>
                        )}
                        <button title="Download CSV" className="p-1 md:p-2 rounded-full hover:bg-[#c8d8e4] text-[#2b6777] transition-all duration-200 hover:scale-110" onClick={handleDownloadCSV}>
                          <FaDownload size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        
        <LocationFormModal
          open={showForm}
          onClose={() => setShowForm(false)}
          onSubmit={handleFormSubmit}
          initialData={selectedLocation}
          isEdit={isEdit}
          loading={formLoading}
        />
        <LocationViewModal
          open={showView}
          onClose={() => setShowView(false)}
          location={selectedLocation}
        />
      </div>
    </main>
  </div>
);
};

export default LocationMaster; 